/**
 * Domain Layer - UI Slice
 * Redux slice for UI state management
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uuid } from "@/shared/core/utils";
import type { Theme, NotificationType } from "@/shared/core/types";

/**
 * Notification entity
 */
export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
  read: boolean;
}

/**
 * UI state shape
 */
export interface UiState {
  sidebarOpen: boolean;
  currentTheme: Theme;
  isOnline: boolean;
  isMobileMenuOpen: boolean;
  activeModal: string | null;
  notifications: Notification[];
}

/**
 * Initial UI state
 */
const initialState: UiState = {
  sidebarOpen: false,
  currentTheme: "light",
  isOnline: true,
  isMobileMenuOpen: false,
  activeModal: null,
  notifications: [],
};

/**
 * UI slice with reducers
 */
const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    // Sidebar
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },

    // Theme
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.currentTheme = action.payload;
    },

    // Online status
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },

    // Mobile menu
    toggleMobileMenu: (state) => {
      state.isMobileMenuOpen = !state.isMobileMenuOpen;
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.isMobileMenuOpen = action.payload;
    },

    // Modal
    setActiveModal: (state, action: PayloadAction<string | null>) => {
      state.activeModal = action.payload;
    },

    // Notifications
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, "id" | "read">>
    ) => {
      const id = uuid();
      state.notifications.push({
        ...action.payload,
        id,
        read: false,
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      );
      if (notification) {
        notification.read = true;
      }
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
});

// Export actions
export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  setOnlineStatus,
  toggleMobileMenu,
  setMobileMenuOpen,
  setActiveModal,
  addNotification,
  removeNotification,
  markNotificationAsRead,
  clearNotifications,
} = uiSlice.actions;

// Export reducer
export default uiSlice.reducer;
